import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
  type QueryClient,
} from "@tanstack/react-query";

import { api, ApiError, withQuery } from "./api";
import { isInFlight } from "./types";
import type {
  Collection,
  CollectionDetail,
  Entry,
  EntryFilters,
  EntryPage,
  EntryPatch,
  Manifest,
  Me,
  MePatch,
} from "./types";

/** ~2s while a photograph is developing; classification lands in 8–20s. */
export const ENTRY_POLL_MS = 2000;
/** ~3s while Claude writes the hunt list. */
export const QUARRY_POLL_MS = 3000;

/* -------------------------------------------------------------------------- */
/* Query keys                                                                 */
/* -------------------------------------------------------------------------- */

/**
 * `entries` and `activeEntries` deliberately live in different key namespaces:
 * everything under `["entries", …]` holds `InfiniteData<EntryPage>`, so cache
 * surgery over that prefix can be typed without shape sniffing.
 */
export const qk = {
  me: ["me"] as const,
  manifest: ["manifest"] as const,
  entries: (filters: EntryFilters = {}) => ["entries", filters] as const,
  entry: (id: string) => ["entry", id] as const,
  activeEntries: ["active-entries"] as const,
  collections: ["collections"] as const,
  collection: (id: string) => ["collection", id] as const,
};

/* -------------------------------------------------------------------------- */
/* Cache surgery                                                              */
/* -------------------------------------------------------------------------- */

type EntryInfinite = InfiniteData<EntryPage, string | null>;

/** Write an entry into every cached place it might already appear. */
export function writeEntry(client: QueryClient, entry: Entry): void {
  client.setQueryData<Entry>(qk.entry(entry.id), entry);

  client.setQueriesData<EntryInfinite>({ queryKey: ["entries"] }, (old) => {
    if (!old) return old;
    let touched = false;
    const pages = old.pages.map((page) => {
      if (!page.items.some((item) => item.id === entry.id)) return page;
      touched = true;
      return { ...page, items: page.items.map((item) => (item.id === entry.id ? entry : item)) };
    });
    return touched ? { ...old, pages } : old;
  });

  client.setQueryData<Entry[]>(qk.activeEntries, (old) =>
    old ? old.map((item) => (item.id === entry.id ? entry : item)) : old,
  );
}

/** Remove a deleted entry from every cached list. */
export function forgetEntry(client: QueryClient, entryId: string): void {
  client.removeQueries({ queryKey: qk.entry(entryId) });

  client.setQueriesData<EntryInfinite>({ queryKey: ["entries"] }, (old) => {
    if (!old) return old;
    return {
      ...old,
      pages: old.pages.map((page) => ({
        ...page,
        items: page.items.filter((item) => item.id !== entryId),
      })),
    };
  });

  client.setQueryData<Entry[]>(qk.activeEntries, (old) =>
    old ? old.filter((item) => item.id !== entryId) : old,
  );
}

/** Only `placeQuery` can change which collections an entry belongs to. */
function invalidateFiling(client: QueryClient, entryId: string): void {
  void client.invalidateQueries({ queryKey: qk.entry(entryId) });
  void client.invalidateQueries({ queryKey: ["entries"] });
  void client.invalidateQueries({ queryKey: qk.collections });
  void client.invalidateQueries({ queryKey: ["collection"] });
  void client.invalidateQueries({ queryKey: qk.me });
}

/* -------------------------------------------------------------------------- */
/* Auth                                                                       */
/* -------------------------------------------------------------------------- */

export type MeResult = {
  me: Me | null;
  /** A 401 is a normal state, not an error worth a toast. */
  isUnauthenticated: boolean;
  isLoading: boolean;
  /** A real problem: the API is down, or something unexpected came back. */
  error: ApiError | null;
  refetch: () => void;
};

export function useMe(): MeResult {
  const query = useQuery({
    queryKey: qk.me,
    queryFn: ({ signal }) => api.get<Me>("/me", signal),
    // Never retry: a 401 is an answer, and retrying it three times just makes
    // the landing page slower to appear.
    retry: false,
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: true,
  });

  const apiError = query.error instanceof ApiError ? query.error : null;
  const isUnauthenticated = apiError?.status === 401;

  return {
    me: query.data ?? null,
    isUnauthenticated,
    isLoading: query.isPending,
    error: isUnauthenticated ? null : apiError,
    refetch: () => void query.refetch(),
  };
}

export function useLogout() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<void>("/auth/logout"),
    onSuccess: () => {
      // The cookie is gone; so is everything we cached under it.
      client.clear();
    },
  });
}

/** Sets or clears the account's default place — `{ placeQuery: "" }` clears it. */
export function useUpdateMe() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (patch: MePatch) => api.patch<Me>("/me", patch),
    onSuccess: (me) => {
      client.setQueryData<Me>(qk.me, me);
    },
  });
}

/* -------------------------------------------------------------------------- */
/* Manifest                                                                   */
/* -------------------------------------------------------------------------- */

export function useManifest() {
  return useQuery({
    queryKey: qk.manifest,
    queryFn: ({ signal }) => api.get<Manifest>("/manifest", signal),
    // Static and cacheable: fetch it once per session.
    staleTime: Infinity,
    gcTime: Infinity,
  });
}

/* -------------------------------------------------------------------------- */
/* Entries                                                                    */
/* -------------------------------------------------------------------------- */

export function useEntries(filters: EntryFilters = {}, enabled = true) {
  return useInfiniteQuery({
    queryKey: qk.entries(filters),
    queryFn: ({ pageParam, signal }) =>
      api.get<EntryPage>(
        withQuery("/entries", { ...filters, cursor: pageParam ?? undefined }),
        signal,
      ),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage: EntryPage) => lastPage.nextCursor,
    enabled,
  });
}

export type UseEntryOptions = {
  /** Poll every ~2s while the entry is still developing, then stop. */
  poll?: boolean;
  enabled?: boolean;
};

export function useEntry(id: string | undefined, options: UseEntryOptions = {}) {
  const { poll = false, enabled = true } = options;

  return useQuery({
    queryKey: qk.entry(id ?? "__none__"),
    queryFn: ({ signal }) => api.get<Entry>(`/entries/${id ?? ""}`, signal),
    enabled: enabled && Boolean(id),
    refetchInterval: poll
      ? (query) => {
          const status = query.state.data?.status;
          if (!status) return ENTRY_POLL_MS;
          return isInFlight(status) ? ENTRY_POLL_MS : false;
        }
      : false,
    // No point polling a tab nobody is looking at.
    refetchIntervalInBackground: false,
  });
}

/**
 * Every non-terminal entry. Used once on app load so a refresh mid-development
 * doesn't lose the developing cards.
 */
export function useActiveEntries(enabled = true) {
  return useQuery({
    queryKey: qk.activeEntries,
    queryFn: ({ signal }) => api.get<Entry[]>("/entries/active", signal),
    enabled,
    staleTime: 0,
    refetchOnWindowFocus: true,
  });
}

type PatchContext = { previous: Entry | undefined };

/**
 * Optimistic by default: notes, titles and favourites all apply instantly and
 * roll back if the server disagrees. `placeQuery` is the exception — only the
 * server knows what a place string geocodes to, and it re-files the entry — so
 * that one waits for the response and then invalidates the filing.
 */
export function usePatchEntry(entryId: string) {
  const client = useQueryClient();

  return useMutation<Entry, unknown, EntryPatch, PatchContext>({
    mutationFn: (patch: EntryPatch) => api.patch<Entry>(`/entries/${entryId}`, patch),

    onMutate: async (patch) => {
      await client.cancelQueries({ queryKey: qk.entry(entryId) });
      const previous = client.getQueryData<Entry>(qk.entry(entryId));

      if (previous) {
        const optimistic: Entry = { ...previous };
        if (patch.userTitle !== undefined) {
          optimistic.userTitle = patch.userTitle;
          const fallback = previous.title ?? previous.commonName ?? previous.displayTitle;
          optimistic.displayTitle =
            patch.userTitle && patch.userTitle.trim().length > 0 ? patch.userTitle : fallback;
        }
        if (patch.notes !== undefined) optimistic.notes = patch.notes;
        if (patch.favourite !== undefined) optimistic.favourite = patch.favourite;
        writeEntry(client, optimistic);
      }

      return { previous };
    },

    onError: (_error, _patch, context) => {
      if (context?.previous) writeEntry(client, context.previous);
    },

    onSuccess: (entry, patch) => {
      writeEntry(client, entry);
      if (patch.placeQuery !== undefined) invalidateFiling(client, entryId);
    },
  });
}

export function useDeleteEntry() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (entryId: string) => api.del(`/entries/${entryId}`),
    onSuccess: (_data, entryId) => {
      forgetEntry(client, entryId);
      void client.invalidateQueries({ queryKey: qk.collections });
      void client.invalidateQueries({ queryKey: ["collection"] });
      void client.invalidateQueries({ queryKey: qk.me });
    },
  });
}

export function useRetryEntry() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (entryId: string) => api.post<Entry>(`/entries/${entryId}/retry`),
    onSuccess: (entry) => {
      writeEntry(client, entry);
      void client.invalidateQueries({ queryKey: qk.activeEntries });
    },
  });
}

/* -------------------------------------------------------------------------- */
/* Collections                                                                */
/* -------------------------------------------------------------------------- */

export function useCollections(enabled = true) {
  return useQuery({
    queryKey: qk.collections,
    queryFn: ({ signal }) => api.get<Collection[]>("/collections", signal),
    enabled,
    staleTime: 30_000,
  });
}

export function useCollection(id: string | undefined) {
  return useQuery({
    queryKey: qk.collection(id ?? "__none__"),
    queryFn: ({ signal }) => api.get<CollectionDetail>(`/collections/${id ?? ""}`, signal),
    enabled: Boolean(id),
    // Claude is still drawing up the hunt list: check back every few seconds.
    refetchInterval: (query) => (query.state.data?.quarryState === "pending" ? QUARRY_POLL_MS : false),
    refetchIntervalInBackground: false,
  });
}

export function useRefreshQuarry(collectionId: string) {
  const client = useQueryClient();
  return useMutation({
    // 202: the work happens in the background and quarryState flips to pending.
    mutationFn: () => api.post<void>(`/collections/${collectionId}/quarry/refresh`),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: qk.collection(collectionId) });
    },
  });
}
